<svelte:window on:resize={onResize} />

<Page contentAreaLayout="wide">
    <PageHeader title={courseInfo.title} backLinkText={i18n.backToCourseCatalogue} backLinkUrl={backLinkUrl} />

    <Banner type='info' text={bannerText} visible={!!bannerText} closable={false} />
    <Banner type='info' text={i18n.courseAvailableOutside} visible={displayOptions.showAvailableExternally} closable={false} />
    <Banner type='info' text={i18n.externalEnrollmentApplicationSuccessBanner} visible={displayOptions.showExternalEnrollmentApplicationSuccess} closable={false} />
    <Banner type='info' text={i18n.courseForTeachersOnly} visible={displayOptions.showTeachersOnlyBanner} closable={false} />
    <Banner type='info' text={i18n.courseForStudentsOnly} visible={displayOptions.showStudentsOnlyBanner} closable={false} />
    <Banner type='info' visible={infoVisible} closable={false}>
        {messages.info}
        {#if displayOptions.showLinkToCourse && courseInfo.courseInternalUrl}
            <Link kind="standalone" href={courseInfo.courseInternalUrl} target="_top" text={courseInfo.title}/>.
        {/if}
    </Banner>
    <Banner type='warning' text={messages.error} visible={errorVisible} close={i18n.backToClose} />

    <div class="its-course-catalogue-info__content">
        <div class="its-course-catalogue-info__side-bar">
            {#if courseInfo.imageUrl}
                <div bind:this={courseImageElement} class="its-course-catalogue-info__image" style={courseImageStyle}></div>
            {/if}
            <div class="its-course-catalogue-info__details">
                {#if courseInfo.teachers && courseInfo.teachers.length > 0}
                    <div>
                        <div class="its-course-catalogue-info__label">{i18n.teacher}</div>
                        <div class="its-course-catalogue-info__teachers">
                            {#each visibleTeachers as teacher}
                                <Profile name={teacher.fullName} src={teacher.personPictureUrl} size={200}/>
                            {/each}

                            {#if hasMoreThanThree}
                                <IconButton
                                expanded = {showAll}
                                disabled={false}
                                iconClass={showAll ? "course-catalogue-info__show-less-button-icon" : "course-catalogue-info__show-more-button-icon"}
                                text={showAll ? i18n.showLess : i18n.showAllTeachers}
                                type="plain"
                                on:click={() => showAll = !showAll} />
                            {/if}
                        </div>
                    </div>
                {/if}
                <div class="its-course-catalogue-info__buttons">
                    {#if displayOptions.showEnrolButton }
                        <Button type="primary" text={i18n.enrolInTheCourse} on:click={enrolInTheCourse}/>
                    {/if}
                    {#if displayOptions.showWithdrawButton }
                        <Button type="secondary" text={i18n.withdrawFromCourse} on:click={withdrawFromCourse}/>
                    {/if}
                    {#if displayOptions.showCancelEnrollmentButton }
                        <Button type="secondary" text={i18n.cancelEnrollment} on:click={cancelEnrollmentFromCourse}/>
                    {/if}
                    {#if displayOptions.showShareButton}
                        <IconButton
                            ariaLabel={i18n.shareLinkToCourse}
                            disabled={false}
                            iconClass="course-catalogue-info__share-button-icon"
                            text={i18n.shareLinkToCourse}
                            type="secondary"
                            on:click={shareCourse} />
                    {/if}
                </div>
            </div>
        </div>
        <div class="its-course-catalogue-info__body">
            <div>
                {#if courseInfo.code}
                    <div class="its-course-catalogue-info__field">
                        <div class="its-course-catalogue-info__label">{i18n.courseCode}</div>
                        <p>{courseInfo.code}</p>
                    </div>
                {/if}
                <div class="its-course-catalogue-info__field">
                    <div class="its-course-catalogue-info__label">{i18n.description}</div>
                    <p>{courseInfo.content}</p>
                </div>
                {#if courseInfo.teachers && courseInfo.teachers.length > 0}
                    <div class="its-course-catalogue-info__field">
                        <div class="its-course-catalogue-info__label">{i18n.contact}</div>
                        <div class="its-course-catalogue-info__contacts">
                            {#each courseInfo.teachers.filter(t => t.isContactPersonInCatalogue) as contact}
                                <Profile name={contact.fullName} src={contact.personPictureUrl} size={200}/>
                            {/each}
                        </div>
                    </div>
                {/if}
                {#if courseInfo.subject}
                    <div class="its-course-catalogue-info__field">
                        <div class="its-course-catalogue-info__label">{i18n.subject}</div>
                        <p>{courseInfo.subject}</p>
                    </div>
                {/if}
                {#if courseInfo.educationalLevel}
                    <div class="its-course-catalogue-info__field">
                        <div class="its-course-catalogue-info__label">{i18n.educationalLevel}</div>
                        <p>{courseInfo.educationalLevel}</p>
                    </div>
                {/if}
                {#if courseInfo.targetRole}
                    <div class="its-course-catalogue-info__field">
                        <div class="its-course-catalogue-info__label">{i18n.targetRole}</div>
                        <p>{courseInfo.targetRole}</p>
                    </div>
                {/if}
                {#if courseInfo.organisation}
                    <div class="its-course-catalogue-info__field">
                        <div class="its-course-catalogue-info__label">{i18n.organisation}</div>
                        <p>{courseInfo.organisation}</p>
                    </div>
                {/if}
            </div>
        </div>
    </div>
</Page>

<script>
    import {createEventDispatcher, onMount} from 'svelte';

    const dispatch = createEventDispatcher();

    import Banner from '@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte';
    import IconButton from '@itslearning/prometheus/assets/inputs/IconButton/v1/IconButton.svelte';
    import Button from '@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte';
    import Link from "@itslearning/prometheus/assets/navigation/Link/v1/Link.svelte";
    import Page from "@itslearning/prometheus/assets/templates/Page/v1/Page.svelte";
    import PageHeader from '@itslearning/prometheus/assets/patterns/PageHeader/v1/PageHeader.svelte';
    import Profile from "@itslearning/prometheus/assets/nodes/Profile/v1/Profile.svelte";

    /**
     * @typedef {Object} CatalogueCourseInfo
     * @property {string} title
     * @property {string} code
     * @property {string} imageUrl
     * @property {string} content
     * @property {CatalogueCourseTeacherModel[]} teachers
     * @property {string} subject
     * @property {string} educationalLevel
     * @property {string} targetRole
     * @property {string} organisation
     * @property {string} courseInternalUrl
     */

    /**
     * @typedef {Object} CatalogueCourseTeacherModel
     * @property {string} fullName
     * @property {number} personId
     * @property {string} personPictureUrl
     * @property {boolean} isContactPersonInCatalogue
     */

    /**
     * @typedef {Object} CatalogueMessages
     * @property {string} error
     * @property {string} info
     */

    /**
     * @typedef {Object} DisplayOptions
     * @property {boolean} showEnrolButton
     * @property {boolean} showWithdrawButton]
     * @property {boolean} showCancelEnrollmentButton
     * @property {boolean} showShareButton
     * @property {boolean} showAvailableExternally
     * @property {boolean} showLinkToCourse
     * @property {boolean} showExternalEnrollmentApplicationSuccess
     * @property {boolean} showTeachersOnlyBanner
     * @property {boolean} showStudentsOnlyBanner
     * @property {string} banner
     */

    export let i18n = {};

    /** @type CatalogueMessages */
    export let messages = { info: '', error: '' };

    /** @type CatalogueCourseInfo */
    export let courseInfo = {
        title: '',
        code: '',
        imageUrl: '',
        content: '',
        teachers: [],
        subject: '',
        educationalLevel: '',
        targetRole: '',
        organisation: '',
        courseInternalUrl: ''
    };

    /** @type DisplayOptions */
    export let displayOptions = {
        showEnrolButton: false,
        showShareButton: false,
        showWithdrawButton: false,
        showCancelEnrollmentButton: false,
        showAvailableExternally: false,
        showLinkToCourse: false,
        showTeachersOnlyBanner: false,
        showStudentsOnlyBanner: false,
        banner: ''
    };

    /** @type string */
    export let backLinkUrl = '';

    $: errorVisible = !!messages.error;
    $: infoVisible = !!messages.info;

    let courseImageElement = undefined;
    let width = 0;
    let height = 0;
     let showAll = false;

  $: hasMoreThanThree = courseInfo.teachers.length > 3;
  $: visibleTeachers = showAll
    ? courseInfo.teachers
    : courseInfo.teachers.slice(0, 3);

    $: courseImageStyle = `background-image: url('${courseInfo.imageUrl}'); background-color: transparent; height: ${height}px`;

    let bannerText = getBannerText(displayOptions.banner);

    onMount(() => {
        onResize();
    });

    function onResize() {
        if (courseImageElement) {
            width = courseImageElement.getBoundingClientRect().width;
            height = width * 0.5;
        }
    }

    function enrolInTheCourse() {
        dispatch('enrol');
    }

    function withdrawFromCourse() {
        dispatch('withdraw');
    }

    function cancelEnrollmentFromCourse() {
        dispatch('cancelEnrollment');
    }

    function shareCourse() {
        dispatch('share');
    }

    function getBannerText(banner) {
        if (banner === 'AccessExpired') {
            return i18n.accessExpired;
        }

        if (banner === 'ExternalEnrollmentSuccess') {
            return i18n.externalEnrollmentApplicationSuccessBanner;
        }

        if (banner === 'RejectedPermanently') {
            return i18n.rejectedPermanently;
        }

        return "";
    }
</script>
